import {ConditionDTO, ToothConditionDTO, ToothConditionResponseDTO} from "./tooth.types";

export class SneakPeakInfo {
    constructor(
        private x:number,
        private y:number,
        public visible:boolean,
        public conditions: ConditionDTO[],
        public treatments: ConditionDTO[],
        public leftSide:boolean
        ){}
    get X(){return this.x;}
    get Y(){return this.y;}
    reset(){
        this.visible = false;
        this.conditions = [];
        this.treatments = [];
    }
}

export enum StructureChangeType {
    REMOVES="REMOVES", ADDS="ADDS", CHANGES="CHANGES"
}

export enum SurfaceChange {
    YES="YES",
    NO="NO",
}

export enum ConditionInternalIDs{
    //conditions
    NOT_ASSIGNED="NOT_ASSIGNED",
    SURFACE_CAVITY="SURFACE_CAVITY",
    BLEED="BLEED",
    GINGIVITIS="GINGIVITIS",
    PAIN="PAIN",
    FISSURE = "FISSURE",
    MOBILITY = "MOBILITY",
    SENSITIVITY_COLD = "SENSITIVITY_COLD",
    SENSITIVITY_HEAT = "SENSITIVITY_HEAT",
    ABCESS = "ABCESS",
    RESIDUAL_ROOT = "RESIDUAL_ROOT",
    MISSING_TOOTH = "MISSING_TOOTH",
    SUPRA_TOOTH = "SUPRA_TOOTH",
    YELLOW_COLOR = "YELLOW_COLOR",
    ERODED = "ERODED",
    DEPOSITS = "DEPOSITS",
    PULP_INFECTED = "PULP_INFECTED",


    //treatments
    CONSULT = "CONSULT",
    PLAN = "PLAN",
    MEDICATION = "MEDICATION",
    LASER = "LASER",
    EXTRACTION = "EXTRACTION",
    SURFACE_COR_FILL = "SURFACE_COR_FILL",
    SURFACE_COR_FILL_FOREIGN = "SURFACE_COR_FILL_FOREIGN",
    CROWN_PIVOT_RECONSTRUCTION = "CROWN_PIVOT_RECONSTRUCTION",
    ROOT_FILL = "ROOT_FILL",
    DRAINAGE = "DRAINAGE",
    HYPERESTHESIA = "HYPERESTHESIA",
    DETARTRAJ = "DETARTRAJ",
    CROWN_ZIRCONIUM = "CROWN_ZIRCONIUM",
    CROWN_METAL = "CROWN_METAL",
    CROWN_INLINEONE = "CROWN_INLINEONE",
    CROWN_ACRYL = "CROWN_ACRYL",
    CROWN_TEMPORARY = "CROWN_TEMPORARY",
    PIVOT_ZIRCON = "PIVOT_ZIRCON",
    PIVOT_METAL = "PIVOT_METAL",
    IMPLANT = "IMPLANT",
    CEMENTATION_FINAL = "CEMENTATION_FINAL",
    CEMENTATION_TEMPORARY = "CEMENTATION_TEMPORARY",
    PROTEZA_ACRYL = "PROTEZA_ACRYL",
    PROTEZA_METALICA = "PROTEZA_METALICA",
    PROTEZA = "PROTEZA",
    GUTIERA = "GUTIERA",
    DECAPUSONARE = "DECAPUSONARE",
}

export class StructureChangeInfo {
    // following properties models the way condition affects tooth.
    crownChange?: StructureChangeType;
    crownGraphicID?: string; /* CAN BE NULL IF crownChange != CHANGES */
    crownCSSCls?: string;
    rootChange?: StructureChangeType;
    rootGraphicID?: string; /* CAN BE NULL IF rootChange != CHANGES */
    rootCSSCls?: string;
    surfaceChange?: SurfaceChange; /* YES/NO */
    surfaceGraphicDir?: string;
    surfaceCSScls?:string;
    overlays?: string; /* semicolon separated value. can be NULL */
    overlaysCSScls?: string; /* semicolon separated value. can be NULL. Multiple css classes can be added before semicolon separation */
    icons?: string; /* semicolon separated value. can be NULL */
    iconsCSScls?: string; /* semicolon separated value. can be NULL. Multiple css classes can be added before semicolon separation */
}

export const structure_changes:Map<ConditionInternalIDs, StructureChangeInfo> =
    new Map([
        [ConditionInternalIDs.NOT_ASSIGNED,      null],
        [ConditionInternalIDs.SURFACE_CAVITY,    { crownChange: StructureChangeType.ADDS, surfaceChange: SurfaceChange.YES, surfaceGraphicDir: "surface"}],
        [ConditionInternalIDs.BLEED,             { icons: "../shared/icons/bleed.png"}],
        [ConditionInternalIDs.PAIN,              { icons: "../shared/icons/pain.png"}],
        [ConditionInternalIDs.SENSITIVITY_COLD,  { icons: "../shared/icons/sensitivity.png"}],
        [ConditionInternalIDs.SENSITIVITY_HEAT,  { icons: "../shared/icons/sensitivity.png"}],
        [ConditionInternalIDs.GINGIVITIS,        { overlays: "advanced/gingivita.png", overlaysCSScls:"hue-rotate-0"}],
        [ConditionInternalIDs.FISSURE,           { overlays: "advanced/crack.png", overlaysCSScls:"hue-rotate-0"}],
        [ConditionInternalIDs.MOBILITY,          { overlays: "../shared/mobility.png", overlaysCSScls:"hue-rotate-0"}],
        [ConditionInternalIDs.ABCESS,            { overlays: "advanced/abcess.png", overlaysCSScls:"hue-rotate-0"}],
        [ConditionInternalIDs.RESIDUAL_ROOT,     { crownChange: StructureChangeType.REMOVES}],
        [ConditionInternalIDs.YELLOW_COLOR,      { crownChange: StructureChangeType.ADDS, crownCSSCls: "saturate-[1.8] brightness-[1.1]"}],

        [ConditionInternalIDs.CROWN_PIVOT_RECONSTRUCTION,  { overlays: "advanced/pivot_fibra.png", overlaysCSScls:"hue-rotate-0"}],
        [ConditionInternalIDs.CROWN_ZIRCONIUM,  { crownChange: StructureChangeType.ADDS, crownCSSCls: "hue-rotate-[100deg]" }],
        [ConditionInternalIDs.CROWN_METAL,      { crownChange: StructureChangeType.ADDS, crownCSSCls: "hue-rotate-[100deg] saturate-0 brightness-[0.65]" }],
        [ConditionInternalIDs.CROWN_ACRYL,      { crownChange: StructureChangeType.ADDS, crownCSSCls: "hue-rotate-[140deg] saturate-[1.3] brightness-[1.1]"}],
        [ConditionInternalIDs.CROWN_INLINEONE,  { crownChange: StructureChangeType.ADDS, crownCSSCls: "hue-rotate-[250deg] saturate-[1.3] brightness-[0.9]"}],
        [ConditionInternalIDs.CROWN_TEMPORARY,  { crownChange: StructureChangeType.ADDS, crownCSSCls: "hue-rotate-[310deg]  saturate-[1.8] brightness-[1.2]"}],

        [ConditionInternalIDs.PIVOT_ZIRCON,     { overlays: "advanced/pivot_titan.png", overlaysCSScls:"hue-rotate-0"}],
        [ConditionInternalIDs.PIVOT_METAL,      { overlays: "advanced/pivot_metal.png", overlaysCSScls:"hue-rotate-0"}],
        [ConditionInternalIDs.IMPLANT,          { overlays: "advanced/implant.png", overlaysCSScls:"hue-rotate-0"}],
        [ConditionInternalIDs.GUTIERA,          { overlays: "advanced/gutiera.png", overlaysCSScls:"hue-rotate-0"}],

        [ConditionInternalIDs.EXTRACTION,       { crownChange:  StructureChangeType.REMOVES, rootChange: StructureChangeType.REMOVES}],
        [ConditionInternalIDs.SURFACE_COR_FILL, { crownChange: StructureChangeType.ADDS, surfaceChange: SurfaceChange.YES, surfaceGraphicDir: "selection", surfaceCSScls: "hue-rotate-[210deg] mix-blend-overlay"}],
        [ConditionInternalIDs.SURFACE_COR_FILL_FOREIGN, { crownChange: StructureChangeType.ADDS, surfaceChange: SurfaceChange.YES, surfaceGraphicDir: "selection", surfaceCSScls: "hue-rotate-[230deg] mix-blend-overlay"}],
        [ConditionInternalIDs.MISSING_TOOTH,    { crownChange:  StructureChangeType.REMOVES, rootChange: StructureChangeType.REMOVES}],
        [ConditionInternalIDs.PROTEZA_ACRYL,    { crownChange: StructureChangeType.ADDS, crownCSSCls: "hue-rotate-[130deg]", rootChange: StructureChangeType.REMOVES }],
        [ConditionInternalIDs.PROTEZA_METALICA, { crownChange: StructureChangeType.ADDS, crownCSSCls: "hue-rotate-[180deg]", rootChange: StructureChangeType.REMOVES }],
        [ConditionInternalIDs.PROTEZA,          { crownChange: StructureChangeType.ADDS, crownCSSCls: "hue-rotate-[180deg]", rootChange: StructureChangeType.REMOVES }],
    ]);

export function getStructureInfo(ID:ConditionInternalIDs):StructureChangeInfo {
    return structure_changes.get(ID);
}
